import React, { useEffect, useMemo, useState } from "react";
import {
  getTransactionApprovals,
  approveTransactionAction,
  rejectTransactionAction,
} from "../../services/api";
import toast from "react-hot-toast";
import "./transactions.css";

export default function TransactionApprovals() {
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [status, setStatus] = useState("PENDING");
  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminComment, setAdminComment] = useState("");
  const [actionType, setActionType] = useState("");

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const res = await getTransactionApprovals();
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error("Failed to load approval requests");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return requests.filter((r) => {
      const statusOk = status === "ALL" ? true : r.approval_status === status;

      const searchOk = !q
        ? true
        : [
            r.transaction_id,
            r.reference_no,
            r.full_name,
            r.member_code,
            r.phone,
            r.action_type,
            r.cashier_username,
            r.cashier_comment,
            r.parokia_name,
            r.kanda_name,
            r.jumuia_name,
          ]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q));

      return statusOk && searchOk;
    });
  }, [requests, status, search]);

  const openApprovalModal = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setAdminComment("");
  };

  const submitAdminDecision = async () => {
    if (!selectedRequest) return;

    try {
      if (actionType === "APPROVE") {
        await approveTransactionAction(selectedRequest.request_id, {
          adminComment,
        });
        toast.success("Transaction action approved");
      }

      if (actionType === "REJECT") {
        await rejectTransactionAction(selectedRequest.request_id, {
          adminComment,
        });
        toast.success("Transaction action rejected");
      }

      setSelectedRequest(null);
      setAdminComment("");
      setActionType("");
      loadRequests();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to process action");
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "-" : d.toLocaleString();
  };

  return (
    <div className="card">
      <div className="card-header between">
        <div>
          <h3 style={{ margin: 0 }}>Transaction Approval Requests</h3>
          <p className="subtitle" style={{ marginTop: 6 }}>
            Approve cashier transaction actions within your Parokia.
          </p>
        </div>
      </div>

      <div className="transaction-filter-grid approval">
        <select
          className="input"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="ALL">All</option>
        </select>

        <input
          className="input"
          placeholder="Search approvals..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="spinner-wrap"><div className="spinner" /></div>
      ) : (
        <div className="transaction-table-wrap">
          <table className="table transaction-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Request</th>
                <th>Transaction</th>
                <th>Member</th>
                <th>Cashier Comment</th>
                <th>Status</th>
                <th>Cashier</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="9" className="empty">
                    No approval requests found
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => (
                  <tr key={r.request_id}>
                    <td>{i + 1}</td>

                    <td>
                      <div><strong>Action:</strong> {r.action_type}</div>
                      <div><strong>Request ID:</strong> {r.request_id}</div>
                    </td>

                    <td>
                      <div><strong>Ref:</strong> {r.reference_no || `TRX-${r.transaction_id}`}</div>
                      <div><strong>Amount:</strong> {Number(r.amount || 0).toLocaleString()}</div>
                      <div><strong>Type:</strong> {r.contribution_type}</div>
                      <div><strong>Channel:</strong> {r.channel}</div>
                    </td>

                    <td>
                      <div><strong>Name:</strong> {r.full_name}</div>
                      <div><strong>Code:</strong> {r.member_code}</div>
                      <div><strong>Phone:</strong> {r.phone}</div>
                    </td>

                    <td>{r.cashier_comment || "-"}</td>

                    <td>
                      <span className={`approval-badge ${String(r.approval_status).toLowerCase()}`}>
                        {r.approval_status}
                      </span>
                    </td>

                    <td>{r.cashier_username || "-"}</td>
                    <td>{formatDate(r.created_at)}</td>

                    <td>
                      {r.approval_status === "PENDING" ? (
                        <div className="approval-actions">
                          <button
                            className="btn success small"
                            onClick={() => openApprovalModal(r, "APPROVE")}
                          >
                            Approve
                          </button>

                          <button
                            className="btn danger small"
                            onClick={() => openApprovalModal(r, "REJECT")}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="subtitle">Done</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {selectedRequest && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 650 }}>
            <div className="modal-close" onClick={() => setSelectedRequest(null)}>
              ✕
            </div>

            <h3>
              {actionType === "APPROVE" ? "Approve Transaction Action" : "Reject Transaction Action"}
            </h3>

            <div className="modal-section">
              <p>
                <strong>Action:</strong> {selectedRequest.action_type}
              </p>
              <p>
                <strong>Cashier:</strong> {selectedRequest.cashier_name}
              </p>
              <p>
                <strong>Cashier Comment:</strong> {selectedRequest.cashier_comment}
              </p>
            </div>

            <div className="modal-section">
              <h4>Parokia Admin Comment</h4>
              <textarea
                className="input"
                style={{ minHeight: 100 }}
                placeholder="Optional admin comment"
                value={adminComment}
                onChange={(e) => setAdminComment(e.target.value)}
              />
            </div>

            <div className="modal-actions">
              <button className="btn light" onClick={() => setSelectedRequest(null)}>
                Cancel
              </button>

              <button
                className={actionType === "APPROVE" ? "btn success" : "btn danger"}
                onClick={submitAdminDecision}
              >
                {actionType === "APPROVE" ? "Approve" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}